import DataQuery from "data/DataQuery";
import { Contract_ContractHeatConsumptionMessages, Contract_ContractHeatConsumptionTypes } from "data/endpoints";
import { useQuery } from "data/hooks/useQuery";
import HeatConsumptionsGraph from "features/consumption/components/HeatConsumptionsGraph";
import { HeatConsumptionType } from "features/consumption/enums/heatConsumptionType";
import { useHeatConsumptions } from "features/consumption/hooks/useHeatConsumptions";
import PageCard from "features/shared/components/card/PageCard";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import { containsKey, isEmptyOrUndf } from "features/shared/helpers/objects";
import { getRouteParams } from "features/shared/helpers/routeParams";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import YearSelect from "features/shared/components/selectInput/yearSelect/YearSelect";
import MonthSelect from "features/shared/components/selectInput/monthSelect/MonthSelect";
import MeasuredDataHeader from "features/shared/components/measuredData/MeasuredDataHeader";
import { getEndDate, getMonthBoundaries } from "features/shared/components/measuredData/measuredDataHelper";
import NumberFormatter from "features/shared/components/formatters/NumberFormatter";
import HeatConsumptionMessageDTO from "features/consumption/dtos/HeatConsumptionMessageDTO";
import { getDateStrByParts } from "features/shared/helpers/dateHelper";
import HeatConsumptionMessage from "features/consumption/components/HeatConsumptionMessage";
import Alert from "features/shared/alerts/components/Alert";
import { usePromiseTracker } from "react-promise-tracker";

type Props = {
  contractId: string;
  effectiveDate: Date;
  terminationDate: Date;
};

const HeatConsumptionHistorySection = ({
  contractId,
  effectiveDate,
  terminationDate,
}: Props) => {
  // translations
  const tLabels = useTranslation("contracts\\contracts").t;

  // state init values
  const endDate = getEndDate(terminationDate, 1)
  const boundaries = getMonthBoundaries(
    effectiveDate,
    endDate,
    endDate.getFullYear()
  );

  const [selectedYear, setSelectedYear] = useState<number>(endDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(boundaries[1]);
  const [monthBoundaries, setMonthBoundaries] = useState<number[]>(boundaries);

  // promise tracker hook
  const { promiseInProgress } = usePromiseTracker();

  // fetch data
  // heat consumption types
  const typesQuery: DataQuery = {
    url: Contract_ContractHeatConsumptionTypes,
    routeParams: getRouteParams("contractId", contractId),
  };
  const { data: heatTypes, loadStatus: heatTypesStatus } = useQuery<
    HeatConsumptionType[]
  >(typesQuery, []);

  // heat consumption messages
  const messagesQuery: DataQuery = {
    url: Contract_ContractHeatConsumptionMessages,
    routeParams: getRouteParams("contractId", contractId),
    params: new URLSearchParams({
      From: getDateStrByParts(selectedYear, selectedMonth, "first"),
      To: getDateStrByParts(selectedYear, selectedMonth, "last"),
    }),
  };
  const { data: heatMessages } = useQuery<
  HeatConsumptionMessageDTO[]
  >(messagesQuery, [], [selectedYear, selectedMonth]);

  // heat consumptions
  const heatConsumptionsResult = useHeatConsumptions(
    contractId,
    heatTypes,
    selectedYear,
    selectedMonth
  );

  // helpers
  const hasConsumptionsData = (): boolean => {
    let result = false;

    if (heatTypes.length > 0) {
      for (const type of heatTypes) {
        if (!containsKey(heatConsumptionsResult.heatConsumptions, type)) {
          result = false;
          break;
        }
        const consumptions = heatConsumptionsResult.heatConsumptions[type].measuredData?.measuredValues;

        if (consumptions && consumptions.length > 0) {
          result = true;
        
        }
      }
    }

    return result;
  };

  const getTemperatureUnit = (unit: string) => { 
    switch (unit) {
      case "Celsius":
        return "°C";
      default:
        return "";
    }
  }

  // handlers
  const yearChangeHandler = (year: number) => {
    let actualYearBoundaries = getMonthBoundaries(
      effectiveDate,
      endDate,
      year
    );

    setSelectedYear(year);
    setMonthBoundaries(actualYearBoundaries);
    setSelectedMonth(actualYearBoundaries[1]);
  };

  // sub-renders
  const renderYearSelect = () => {
    return (
      <YearSelect
        startDate={effectiveDate}
        endDate={endDate}
        selectedYear={selectedYear}
        className="w-100"
        onChange={(e) => yearChangeHandler(Number(e.target.value))}
      />
    );
  };

  const renderMonthSelect = () => {
    return (
      <MonthSelect
        startMonth={monthBoundaries[0]}
        endMonth={monthBoundaries[1]}
        selectedMonth={selectedMonth}
        className="w-100"
        onChange={(e) => setSelectedMonth(Number(e.target.value))}
      />
    );
  };

  const renderAverageTemperature = () => {
    let heatType = heatTypes[0];
    let averageTemp =
      heatConsumptionsResult.heatConsumptions[heatType]?.averageValue;
    
    if (!isEmptyOrUndf(averageTemp!)) {
      return (
        <div className="text-right">
          <p className="jumbotron d-inline-block small py-1 px-2 text-muted mt-1 mb-1 mr-2">
            {tLabels("heatConsumptionHistorySection.averageMontTemperature")}
            <strong>
              {" "}
              <NumberFormatter
                amount={averageTemp?.value!}
                decimalPlaces={1}
              />{" "}
              {getTemperatureUnit(averageTemp?.unit!)}
            </strong>
          </p>
        </div>
      );
    }
  };
  
  const renderMessages = (className?: string) => { 
    if (heatMessages?.length > 0 && promiseInProgress === false) {
      return heatMessages.map((item, index) =>
        <HeatConsumptionMessage
          message={item}
          className={className}
          key={index}
        />
      )
    }
  }

  
  const renderNoContentAlert = () => {
    if (heatMessages?.length > 0) {
      return <Alert alertObj={{ alertType: "Info" }}>{renderMessages()}</Alert>;
    } else {
      return (
        <Alert
          alertObj={{
            alertType: "Info",
            message: tLabels(
              "heatConsumptionHistorySection.noConsumptionsData"
            ),
          }}
        />
      );
    }
  };

  // render
  return (
    <NoContentWrapper
      loadStatus={heatTypesStatus}
      data={heatTypes}
      noContentAsAlert
    >
      <MeasuredDataHeader
        renderYearSelect={renderYearSelect}
        renderMonthSelect={renderMonthSelect}
        renderDownloadExcelBtn={() => undefined}
      />
      <NoContentWrapper
        loadStatus={heatConsumptionsResult.loadStatus}
        data={heatConsumptionsResult.heatConsumptions}
        isEmpty={() => !hasConsumptionsData()}
        noContentRender={renderNoContentAlert}
      >
        <PageCard title={tLabels("heatConsumptionHistorySection.cardTitle")}>
          {renderAverageTemperature()}
          <HeatConsumptionsGraph
            heatConsumptions={heatConsumptionsResult.heatConsumptions}
          />
          {renderMessages("text-muted ml-2 mb-2")}
        </PageCard>
      </NoContentWrapper>
    </NoContentWrapper>
  );
};

export default HeatConsumptionHistorySection;
